/**
 * Phase 8 — AI layer canonical constants.
 *
 * Mirrors the shape of `work-order-constants.ts` and `violation-constants.ts`:
 * literal arrays drive both Postgres enums (via Drizzle `pgEnum`) and Zod
 * parsers across API + worker + web. Adding or removing a value requires a
 * coordinated migration; this file is the cross-process source of truth.
 */

// ---------- Features ----------

/**
 * The five distinct AI tasks the platform performs. `voice_to_wo` from the
 * settings UI is one user-facing toggle but maps to two underlying calls
 * (`voice_transcribe` + `voice_structure`), so the feature ID list below
 * is not a 1:1 of the settings payload — it's the call-classification list.
 */
export const AI_FEATURES = [
  'email_parse',
  'wo_classify',
  'violation_summarize',
  'voice_transcribe',
  'voice_structure',
  'compliance_doc_parse',
] as const;
export type AiFeature = (typeof AI_FEATURES)[number];

export const AI_FEATURE_LABELS: Record<AiFeature, string> = {
  email_parse: 'Vendor email parsing',
  wo_classify: 'Work order classification',
  violation_summarize: 'Violation summarization',
  voice_transcribe: 'Voice transcription',
  voice_structure: 'Voice → work order',
  compliance_doc_parse: 'Compliance certificate parsing',
};

/** ≥ this confidence: auto-select suggested building/device in the confirm UI. */
export const COMPLIANCE_DOC_AUTO_SELECT_THRESHOLD = 0.75;
/** Below this: force human pick from dropdowns (never auto-commit). */
export const COMPLIANCE_DOC_REVIEW_THRESHOLD = 0.55;

// ---------- Providers ----------

export const AI_PROVIDERS = ['anthropic', 'openai'] as const;
export type AiProvider = (typeof AI_PROVIDERS)[number];

// ---------- Call status ----------

/**
 * Every `ai_calls` row lands in one of these. `quota_blocked` is what the
 * runner writes when `checkQuota` short-circuits the call before it ever
 * leaves the process — present so super-admin dashboards can see the
 * suppressed traffic, not just the successful traffic.
 */
export const AI_CALL_STATUSES = ['success', 'error', 'cancelled', 'quota_blocked'] as const;
export type AiCallStatus = (typeof AI_CALL_STATUSES)[number];

// ---------- Inbound email pipeline ----------

export const INBOUND_EMAIL_STATUSES = [
  'pending',
  'matched',
  'review_pending',
  'unmatched',
  'ignored',
  'error',
] as const;
export type InboundEmailStatus = (typeof INBOUND_EMAIL_STATUSES)[number];

export const INBOUND_EMAIL_STATUS_LABELS: Record<InboundEmailStatus, string> = {
  pending: 'Pending',
  matched: 'Matched',
  review_pending: 'Needs review',
  unmatched: 'Unmatched',
  ignored: 'Ignored',
  error: 'Error',
};

/**
 * Suggested next-action labels that the email-parse model emits inside
 * its tool response. The worker maps these to concrete commits; the
 * review-queue UI also surfaces them so a reviewer can see what the AI
 * intended to do.
 */
export const INBOUND_EMAIL_SUGGESTED_ACTIONS = [
  'add_comment',
  'add_photos',
  'request_signoff',
  'unmatched',
] as const;
export type InboundEmailSuggestedAction = (typeof INBOUND_EMAIL_SUGGESTED_ACTIONS)[number];

// ---------- Confidence thresholds ----------

/** ≥ this confidence: the worker auto-commits the suggested action to the WO. */
export const INBOUND_EMAIL_AUTO_COMMIT_THRESHOLD = 0.85;
/** ≥ this confidence (but < auto-commit): the email goes to the review queue. */
export const INBOUND_EMAIL_REVIEW_THRESHOLD = 0.6;

/** Default auto-apply threshold for `wo_classify` when the org enables it. */
export const DEFAULT_AUTO_APPLY_CLASSIFICATION_THRESHOLD = 0.9;

// ---------- Token / spend caps (defaults) ----------

/**
 * Per-call hard caps on Anthropic token budgets. The runner clips both
 * sides of the conversation; a feature that needs more headroom should
 * bump these explicitly, not silently exceed them.
 */
export const AI_PER_CALL_TOKEN_CAPS: Record<
  Exclude<AiFeature, 'voice_transcribe'>,
  { input: number; output: number }
> = {
  email_parse: { input: 8000, output: 2000 },
  wo_classify: { input: 4000, output: 1000 },
  violation_summarize: { input: 4000, output: 800 },
  voice_structure: { input: 8000, output: 1200 },
  compliance_doc_parse: { input: 12000, output: 2000 },
};

/** Default per-org daily and monthly USD caps. Editable by the org admin
 *  up to the super-admin-set ceiling. */
export const DEFAULT_DAILY_USD_CAP = 5.0;
export const DEFAULT_MONTHLY_USD_CAP = 100.0;

/** Super-admin-set ceiling. An org admin cannot raise their own cap above
 *  this. Editable per-deployment via env vars. */
export const SUPER_ADMIN_DAILY_CEILING = 25.0;
export const SUPER_ADMIN_MONTHLY_CEILING = 500.0;

/** Voice-memo guardrails. */
export const VOICE_MEMO_MAX_SECONDS = 60;
/** Audio under this duration is transcribed synchronously (the user waits);
 *  longer clips are punted to the worker queue. */
export const VOICE_TRANSCRIBE_SYNC_THRESHOLD_SECONDS = 25;

// ---------- Inbound email address format ----------

/** Compose `updates+{slug}@{domain}`. Org slug is reused unchanged. */
export function composeInboundEmail(orgSlug: string, domain: string): string {
  return `updates+${orgSlug}@${domain}`;
}

/**
 * Pull the org slug from a `to:` address that came through the inbound
 * webhook. Returns `null` when the local part is malformed or the
 * domain doesn't match. Case-insensitive on the domain; the `+` tag
 * carries the slug verbatim (slugs are already lowercase per Phase 0).
 */
export function parseInboundEmail(
  toEmail: string,
  domain: string,
): { slug: string } | null {
  const at = toEmail.indexOf('@');
  if (at <= 0) return null;
  const local = toEmail.slice(0, at).toLowerCase();
  const host = toEmail.slice(at + 1).toLowerCase();
  if (host !== domain.toLowerCase()) return null;
  const plus = local.indexOf('+');
  if (plus < 0) return null;
  if (local.slice(0, plus) !== 'updates') return null;
  const slug = local.slice(plus + 1);
  if (!slug || !/^[a-z0-9-]+$/.test(slug)) return null;
  return { slug };
}
